import { communicationRequest } from "../../utils/contactCommunication";
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { BoltOutlined as BoltOutlinedIcon } from "../icons/FigmaControlIcons";
import AttachFileRoundedIcon from "@mui/icons-material/AttachFileRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import DataObjectRoundedIcon from "@mui/icons-material/DataObjectRounded";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import MoreVertRoundedIcon from "@mui/icons-material/MoreVertRounded";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import { CloseCircle, MessageText1 } from "iconsax-react";
import { MessageComposerAssistRow } from "./MessagesSidebarWidgets";

const BLUE = "#3451F4";
const PURPLE = "#9C4FFF";
const PURPLE_DARK = "#7C3AED";
const BORDER = "#E5E7EB";
const MUTED = "#98A2B3";
const TEXT = "#344054";
const SURFACE = "#FFFFFF";

const sectionLabelSx = {
  color: BLUE,
  fontSize: 11.5,
  fontWeight: 700,
  mb: 0.6,
};

function ContextField({ label, value }) {
  return (
    <Box>
      <Typography sx={{ color: BLUE, fontSize: 11, fontWeight: 700, mb: 0.45 }}>
        {label}
      </Typography>
      <TextField
        size="small"
        fullWidth
        value={value || ""}
        InputProps={{ readOnly: true }}
        sx={{
          "& .MuiOutlinedInput-root": {
            height: 35,
            borderRadius: "6px",
            backgroundColor: SURFACE,
          },
          "& input": {
            color: TEXT,
            fontSize: 12,
            py: 1,
          },
        }}
      />
    </Box>
  );
}

function formatAttachmentSize(value) {
  const size = Number(value || 0);
  if (!Number.isFinite(size) || size <= 0) return "Ready to send";
  if (size < 1024) return `${size} B - ready to send`;
  if (size < 1024 * 1024) return `${Math.max(1, Math.round(size / 1024))} KB - ready to send`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB - ready to send`;
}

function attachmentKey(attachment = {}, index = 0) {
  return (
    attachment.id ||
    `${attachment.name || "attachment"}-${attachment.size || 0}-${attachment.lastModified || index}`
  );
}

export default function NewEmailCandidateDialog({
  open,
  onClose,
  message,
  onSaveDraft,
  onSendLater,
  onSend,
}) {
  const defaults = useMemo(
    () => ({
      to: message?.candidate_email || "",
      cc: message?.cc_email || "",
      bcc: message?.bcc_email || "",
      subject: message?.subject || "",
      body: message?.body_text || "",
      priority: message?.priority || "High",
      attachments: [],
    }),
    [message]
  );

  const [form, setForm] = useState(defaults);
  const fileInputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [aiOpen, setAiOpen] = useState(false);
  const [instructions, setInstructions] = useState("");
  const [suggestion, setSuggestion] = useState(null);
  const runAction = async (handler) => {
    if (busy || !handler) return;
    setError("");
    const recipients = [form.to, form.cc, form.bcc].join(",").split(/[,;]/).map((value) => value.trim()).filter(Boolean);
    if (recipients.some((value) => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value))) {
      setError("Enter valid email addresses separated by commas."); return;
    }
    setBusy(true);
    try { await handler(form); }
    catch (err) { setError(err?.message || "Unable to complete this action."); }
    finally { setBusy(false); }
  };
  const generateDraft = async () => {
    if (busy || !instructions.trim()) return;
    setBusy(true); setError("");
    try {
      const result = await communicationRequest("ai-agent/email-draft", { instructions, context: { ...message, subject: form.subject, body: form.body } });
      setSuggestion(result);
    } catch (err) { setError(err?.message || "Unable to generate an email draft."); }
    finally { setBusy(false); }
  };

  useEffect(() => {
    setForm(defaults);
    setError(""); setSuggestion(null); setAiOpen(false); setInstructions("");
  }, [defaults]);

  const updateField = (key) => (event) => {
    setForm((prev) => ({ ...prev, [key]: event.target.value }));
  };

  const handleAttachFiles = (event) => {
    const nextFiles = Array.from(event.target.files || []);
    if (!nextFiles.length) return;

    setForm((prev) => {
      const existing = Array.isArray(prev.attachments) ? prev.attachments : [];
      const merged = new Map(
        existing.map((attachment, index) => [attachmentKey(attachment, index), attachment])
      );

      nextFiles.forEach((file) => {
        const key = `${file.name}-${file.size}-${file.lastModified}`;
        merged.set(key, {
          id: key,
          name: file.name,
          size: file.size,
          type: file.type || "",
          lastModified: file.lastModified || 0,
          file,
        });
      });

      return {
        ...prev,
        attachments: Array.from(merged.values()),
      };
    });

    event.target.value = "";
  };

  const attachments = Array.isArray(form.attachments) ? form.attachments : [];

  const toolbarItems = [
    {
      key: "ai",
      label: "AI Agent",
      onClick: () => setAiOpen((value) => !value),
      icon: <BoltOutlinedIcon sx={{ fontSize: 14 }} />,
    },
    {
      key: "templates",
      label: "Templates",
      icon: <DescriptionOutlinedIcon sx={{ fontSize: 14 }} />,
    },
    {
      key: "schedule",
      label: "Schedule",
      icon: <ScheduleOutlinedIcon sx={{ fontSize: 14 }} />,
    },
    {
      key: "variables",
      label: "Variables",
      icon: <DataObjectRoundedIcon sx={{ fontSize: 14 }} />,
    },
  ];

  return (
    <Dialog
      open={open}
      onClose={() => !busy && onClose?.()}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: 880,
          maxWidth: "calc(100vw - 32px)",
          borderRadius: 1.5,
          overflow: "hidden",
          boxShadow: "0 24px 64px rgba(16, 24, 40, 0.18)",
        },
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ p: 2.1, backgroundColor: SURFACE }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.6 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box
                sx={{
                  width: 24,
                  height: 20,
                  borderRadius: "7px",
                  border: `1.5px solid ${PURPLE}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <MessageText1 size="13" color={PURPLE} />
              </Box>
              <Typography sx={{ fontSize: 15, color: "#101828", fontWeight: 700 }}>
                Compose Email
              </Typography>
            </Box>

            <Box data-popup-header sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
              <IconButton
                size="small"
                sx={{
                  width: 22,
                  height: 22,
                  border: `1px solid #D7C2FF`,
                  color: PURPLE,
                  p: 0,
                }}
              >
                <KeyboardArrowDownRoundedIcon sx={{ fontSize: 16 }} />
              </IconButton>
              <IconButton data-popup-close size="small" disabled={busy} onClick={onClose} sx={{ p: 0 }}>
                <CloseCircle size="18" color={PURPLE} variant="Bold" />
              </IconButton>
            </Box>
          </Box>

          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1.45fr 0.95fr" }, gap: 2.1 }}>
            <Box>
              <Stack spacing={1.5} sx={{ mb: 2 }}>
                <TextField label="To" fullWidth value={form.to} onChange={updateField("to")} disabled={busy} placeholder="recipient@example.com" />
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                  <TextField label="Cc" fullWidth value={form.cc} onChange={updateField("cc")} disabled={busy} />
                  <TextField label="Bcc" fullWidth value={form.bcc} onChange={updateField("bcc")} disabled={busy} />
                </Stack>
              </Stack>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "auto 1fr",
                  columnGap: 0.8,
                  alignItems: "center",
                  pb: 0.8,
                  mb: 1.05,
                  borderBottom: `1px solid ${BORDER}`,
                }}
              >
                <Typography sx={{ color: BLUE, fontSize: 11, fontWeight: 700 }}>
                  Subject:
                </Typography>

                <TextField
                  disabled={busy}
                  inputProps={{ "aria-label": "Subject" }}
                  variant="standard"
                  fullWidth
                  placeholder="Insert subject"
                  value={form.subject}
                  onChange={updateField("subject")}
                  InputProps={{ disableUnderline: true }}
                  sx={{
                    "& .MuiInputBase-root": {
                      fontSize: 12.5,
                      color: TEXT,
                    },
                    "& .MuiInputBase-input": {
                      p: 0,
                    },
                    "& input::placeholder": {
                      color: MUTED,
                      opacity: 1,
                    },
                  }}
                />
              </Box>

              <MessageComposerAssistRow
                items={toolbarItems}
                variant="filled"
                rightAction={{
                  icon: <MoreVertRoundedIcon sx={{ fontSize: 18, color: PURPLE }} />,
                  onClick: () => {},
                }}
                sx={{
                  pb: 1,
                  mb: 1.1,
                  borderBottom: `1px solid ${BORDER}`,
                }}
              />

              {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
              {aiOpen && <Box sx={{ p: 2, mb: 2, border: `1px solid ${BORDER}`, borderRadius: 2 }}>
                <TextField fullWidth multiline minRows={2} label="What should the email say?" value={instructions}
                  disabled={busy} onChange={(event) => setInstructions(event.target.value)} inputProps={{ maxLength: 4000 }} />
                <Button disabled={busy || !instructions.trim()} onClick={generateDraft}>{busy ? "Working..." : "Generate Draft"}</Button>
                {suggestion && <Box>
                  <Typography sx={{ fontWeight: 600 }}>{suggestion.subject}</Typography>
                  <Typography sx={{ whiteSpace: "pre-wrap", my: 1 }}>{suggestion.body}</Typography>
                  <Button disabled={busy} onClick={() => { setForm((prev) => ({ ...prev, subject: suggestion.subject, body: suggestion.body })); setSuggestion(null); setAiOpen(false); }}>Use Draft</Button>
                </Box>}
              </Box>}
              <Typography sx={sectionLabelSx}>Message:</Typography>

              <TextField
                disabled={busy}
                inputProps={{ "aria-label": "Message" }}
                multiline
                minRows={11}
                fullWidth
                placeholder="Write your email..."
                value={form.body}
                onChange={updateField("body")}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "6px",
                    alignItems: "flex-start",
                    backgroundColor: SURFACE,
                  },
                  "& .MuiInputBase-inputMultiline": {
                    fontSize: 12.5,
                    color: TEXT,
                    lineHeight: 1.55,
                  },
                  "& textarea::placeholder": {
                    color: MUTED,
                    opacity: 1,
                  },
                }}
              />

              <Box sx={{ mt: 1.2 }}>
                <Typography sx={sectionLabelSx}>
                  Attachments:
                </Typography>
                {attachments.length ? (
                  <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                    {attachments.map((attachment, index) => (
                      <Box
                        key={attachmentKey(attachment, index)}
                        sx={{
                          border: `1px solid #C084FC`,
                          borderRadius: "8px",
                          px: 1.05,
                          py: 0.95,
                          display: "inline-flex",
                          alignItems: "flex-start",
                          gap: 0.85,
                          minWidth: 164,
                          maxWidth: "100%",
                          backgroundColor: "#fff",
                        }}
                      >
                        <AttachFileRoundedIcon sx={{ fontSize: 16, color: PURPLE, mt: 0.1 }} />
                        <Box sx={{ minWidth: 0 }}>
                          <Typography
                            sx={{
                              color: TEXT,
                              fontSize: 11,
                              lineHeight: 1.25,
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {attachment.name || "Attachment"}
                          </Typography>
                          <Typography sx={{ color: MUTED, fontSize: 10.5, lineHeight: 1.3 }}>
                            {formatAttachmentSize(attachment.size)}
                          </Typography>
                        </Box>
                        <CheckCircleRoundedIcon sx={{ fontSize: 14, color: PURPLE, mt: 0.1 }} />
                      </Box>
                    ))}
                  </Stack>
                ) : (
                  <Typography sx={{ color: MUTED, fontSize: 11 }}>
                    No attachments added
                  </Typography>
                )}
              </Box>
            </Box>

            <Box>
              <Stack spacing={1.02}>
                <ContextField label="Stage:" value={message?.stage || ""} />
                <ContextField label="Company:" value={message?.company_name || ""} />
                <ContextField
                  label="Contact Info:"
                  value={message?.contact_email || message?.candidate_email || ""}
                />
                <ContextField
                  label="Role:"
                  value={message?.hiring_manager || message?.candidate_title || ""}
                />
                <ContextField label="Lead Status:" value={message?.last_status || ""} />
                <ContextField
                  label="Top Matched Candidate:"
                  value={message?.top_match_label || ""}
                />
              </Stack>
            </Box>
          </Box>

          <Stack
            direction="row"
            justifyContent="flex-end"
            spacing={1}
            sx={{ mt: 1.8, pt: 1.65, borderTop: `1px solid ${BORDER}` }}
          >
            <input
              ref={fileInputRef}
              type="file"
              multiple
              hidden
              onChange={handleAttachFiles}
            />
            <IconButton
              size="small"
              disabled={busy}
              onClick={() => fileInputRef.current?.click()}
              sx={{
                width: 28,
                height: 28,
                border: `1px solid ${PURPLE}`,
                color: PURPLE,
                p: 0,
              }}
            >
              <AddRoundedIcon sx={{ fontSize: 16 }} />
            </IconButton>

            <Button
              type="button"
              variant="outlined"
              disabled={busy || !onSaveDraft}
              onClick={() => runAction(onSaveDraft)}
              startIcon={<SaveOutlinedIcon sx={{ fontSize: 14 }} />}
              sx={{
                textTransform: "none",
                borderRadius: 999,
                borderColor: PURPLE,
                color: PURPLE,
                fontSize: 11.5,
                fontWeight: 700,
                px: 1.5,
                "&:hover": {
                  borderColor: PURPLE,
                  backgroundColor: "#FBF7FF",
                },
              }}
            >
              Save Draft
            </Button>

            {onSendLater && <Button
              type="button"
              disabled={busy}
              className="pill"
              onClick={() => runAction(onSendLater)}
              startIcon={<ScheduleOutlinedIcon sx={{ fontSize: 14 }} />}
              sx={{
                textTransform: "none",
                borderRadius: 999,
                color: "#fff",
                backgroundColor: PURPLE_DARK,
                fontSize: 11.5,
                fontWeight: 700,
                px: 1.6,
                boxShadow: "none",
                "&:hover": {
                  backgroundColor: PURPLE_DARK,
                  boxShadow: "none",
                },
              }}
            >
              Send Later
            </Button>}

            <Button
              type="button"
              disabled={busy || !form.to.trim() || !form.body.trim()}
              className="pill"
              onClick={() => runAction(onSend)}
              startIcon={<SendRoundedIcon sx={{ fontSize: 14 }} />}
              sx={{
                textTransform: "none",
                borderRadius: 999,
                color: "#fff",
                backgroundColor: PURPLE,
                fontSize: 11.5,
                fontWeight: 700,
                px: 1.9,
                boxShadow: "none",
                "&:hover": { backgroundColor: PURPLE, boxShadow: "none" },
              }}
            >
              Send
            </Button>
          </Stack>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
